import React, { useState, useEffect, useMemo } from "react";
import { getApprovedVolunteers } from "../utils/api/volunteerApi.js";
import { useLanguage } from "../context/LanguageContext.jsx";

const INTEREST_AREAS = [
  { ne: "शहरी योजना तथा भौतिक पूर्वाधार",           en: "Urban Planning & Physical Infrastructure" },
  { ne: "सुरक्षा तथा सार्वजनिक सुरक्षा",              en: "Security & Public Safety" },
  { ne: "खानेपानी तथा जल व्यवस्थापन",                en: "Drinking Water & Water Management" },
  { ne: "फोहोर तथा प्रदूषण व्यवस्थापन",               en: "Waste & Pollution Management" },
  { ne: "वन, वातावरण तथा पर्यटन",                    en: "Forest, Environment & Tourism" },
  { ne: "शिक्षा, अनुसन्धान तथा ज्ञान विकास",           en: "Education, Research & Knowledge Development" },
  { ne: "शिल्प, सीप तथा उद्यमशीलता विकास",            en: "Craft, Skill & Entrepreneurship Development" },
  { ne: "स्वास्थ्य तथा जनस्वास्थ्य",                  en: "Health & Public Health" },
  { ne: "स्वस्थ जीवनशैली तथा उपभोक्ता हित संरक्षण",     en: "Healthy Lifestyle & Consumer Rights Protection" },
  { ne: "खेलकुद तथा खेल प्रशिक्षण",                   en: "Sports & Sports Training" },
  { ne: "सूचना प्रविधि तथा स्मार्ट सिटी विकास",         en: "IT & Smart City Development" },
  { ne: "मिडिया तथा सञ्चार",                        en: "Media & Communication" },
  { ne: "व्यापार, उद्योग तथा आर्थिक विकास",           en: "Trade, Industry & Economic Development" },
  { ne: "कृषि तथा सहकारी विकास",                     en: "Agriculture & Cooperative Development" },
  { ne: "विपद् व्यवस्थापन तथा जलवायु अनुकूलन",         en: "Disaster Management & Climate Adaptation" },
  { ne: "कला, संस्कृति तथा पहिचान संरक्षण",            en: "Art, Culture & Identity Preservation" },
  { ne: "सुशासन, आर्थिक नीति तथा वित्तीय व्यवस्थापन",   en: "Good Governance, Economic Policy & Financial Management" },
  { ne: "सामाजिक सुरक्षा तथा समावेशी विकास",           en: "Social Security & Inclusive Development" },
  { ne: "महिला, बालबालिका तथा युवा विकास",            en: "Women, Children & Youth Development" },
  { ne: "अन्तर्राष्ट्रिय सम्बन्ध तथा साझेदारी",         en: "International Relations & Partnership" },
  { ne: "प्रशासन तथा संस्थागत व्यवस्थापन",             en: "Administration & Institutional Management" },
];

const AREA_EN_BY_NE = Object.fromEntries(INTEREST_AREAS.map(a => [a.ne, a.en]));

const CONTENT = {
  ne: {
    pageTitleHighlight: "हाम्रा",
    pageTitleSuffix: "स्वयंसेवकहरू",
    headerDesc: "विभिन्न क्षेत्रमा योगदान गर्न तयार भोलिको इटहरीका स्वयंसेवकहरू।",
    searchPlaceholder: "नाम वा पेशाद्वारा खोज्नुहोस्...",
    allAreas: "सबै रुचि क्षेत्र",
    loading: "स्वयंसेवकहरू लोड हुँदैछ…",
    empty: "कुनै स्वयंसेवक भेटिएन।",
    profession: "पेशा",
    address: "ठेगाना",
    errLoad: "स्वयंसेवकहरू लोड गर्न असफल भयो।",
  },
  en: {
    pageTitleHighlight: "Our",
    pageTitleSuffix: "Volunteers",
    headerDesc: "Volunteers of Bholiko Itahari ready to contribute across sectors.",
    searchPlaceholder: "Search by name or profession...",
    allAreas: "All Interest Areas",
    loading: "Loading volunteers…",
    empty: "No volunteers found.",
    profession: "Profession",
    address: "Address",
    errLoad: "Failed to load volunteers.",
  },
};

const VolunteersListingPage = () => {
  const { language } = useLanguage();
  const t = CONTENT[language] || CONTENT.en;

  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [activeArea, setActiveArea] = useState("All");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getApprovedVolunteers();
        setVolunteers(data);
      } catch (e) {
        setError(e.response?.data?.message || t.errLoad);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function areaLabel(neText) {
    if (!neText) return neText;
    return language === "en" ? (AREA_EN_BY_NE[neText] || neText) : neText;
  }

  // only show areas that at least one approved volunteer actually has
  const availableAreas = useMemo(
    () => Array.from(new Set(volunteers.map(v => v.interestArea).filter(Boolean))),
    [volunteers]
  );

  const filtered = volunteers.filter((v) => {
    const q = search.toLowerCase();
    const matchSearch =
      v.name?.toLowerCase().includes(q) || v.profession?.toLowerCase().includes(q);
    const matchArea = activeArea === "All" || v.interestArea === activeArea;
    return matchSearch && matchArea;
  });

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="max-w-6xl mx-auto px-6 pt-28 pb-12">

        {/* Page title */}
        <div className="mb-6">
          <h1
            className="text-gray-900 text-3xl lg:text-4xl font-bold leading-tight mb-2"
            style={{ fontFamily: "'Playfair Display',serif" }}
          >
            {t.pageTitleHighlight} <span className="text-orange-500">{t.pageTitleSuffix}</span>
          </h1>
          <p className="text-gray-500 text-sm leading-relaxed max-w-lg">
            {t.headerDesc}
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex flex-col sm:flex-row gap-3">

            {/* Search */}
            <div className="relative flex-1">
              <div className="absolute left-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-green-50 flex items-center justify-center pointer-events-none">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgb(21,75,43)" strokeWidth="2.2" strokeLinecap="round">
                  <circle cx="11" cy="11" r="7" />
                  <line x1="16.5" y1="16.5" x2="21" y2="21" />
                </svg>
              </div>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t.searchPlaceholder}
                className="w-full pl-11 pr-9 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 outline-none transition-all focus:bg-white focus:border-green-700 focus:ring-4 focus:ring-green-700/10"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors text-sm"
                >
                  ×
                </button>
              )}
            </div>

            {/* Interest-area dropdown (sector-wise) */}
            <div className="relative sm:w-72">
              <select
                value={activeArea}
                onChange={(e) => setActiveArea(e.target.value)}
                className="w-full appearance-none px-4 py-2.5 pr-9 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 outline-none transition-all focus:bg-white focus:border-green-700 focus:ring-4 focus:ring-green-700/10 cursor-pointer"
              >
                <option value="All">{t.allAreas}</option>
                {availableAreas.map((a) => (
                  <option key={a} value={a}>{areaLabel(a)}</option>
                ))}
              </select>
              <span className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-xs">▾</span>
            </div>

          </div>
        </div>

        {/* Grid */}
        <div className="mt-8">
          {error && (
            <div className="mb-6 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {loading && (
            <div className="text-center py-20 text-gray-400 text-sm">{t.loading}</div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-20 text-gray-400 text-sm">{t.empty}</div>
          )}

          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((v) => (
                <div
                  key={v._id}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all p-6 flex flex-col items-center text-center"
                >
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold text-white mb-4"
                    style={{ background: "rgb(21,75,43)" }}
                  >
                    {v.name?.[0]?.toUpperCase() || "?"}
                  </div>

                  <h3 className="text-base font-bold text-gray-900 mb-1">{v.name}</h3>

                  {v.interestArea && (
                    <span
                      className="inline-block text-[0.7rem] font-semibold px-3 py-1 rounded-full mb-3"
                      style={{ background: "rgba(21,75,43,0.08)", color: "rgb(21,75,43)" }}
                    >
                      {areaLabel(v.interestArea)}
                    </span>
                  )}

                  <div className="w-full text-left text-xs text-gray-500 space-y-1.5 mt-1">
                    {v.profession && (
                      <p><span className="font-semibold text-gray-600">{t.profession}:</span> {v.profession}</p>
                    )}
                    {v.address && (
                      <p><span className="font-semibold text-gray-600">{t.address}:</span> {v.address}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VolunteersListingPage;